/**
 * @fileoverview Audit event detail query hook.
 * @description Wraps getAuditEvent in a React Query query. Only runs while an event is selected,
 * and, like the search, never refetches on focus or retries: every successful detail read is
 * audited and rate-limited (60 a minute per user). A fetched event stays fresh for five minutes so
 * reopening the same row costs nothing.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { AUDIT_QUERY_KEYS } from '@/features/audit/constants/audit-query-keys.constants'
import { getAuditEvent } from '@/features/audit/services/audit.service'

import type { AuditEventDetail } from '@/features/audit/types/audit.types'
import type { ApiError } from '@/shared/types/http.types'

const DETAIL_STALE_TIME_MS = 5 * 60 * 1000

/**
 * @description Fetches one audit event's detail.
 * @param {string} auditEventId - The event to read, or '' for none (the query stays idle).
 * @returns {import('@tanstack/react-query').UseQueryResult<AuditEventDetail, ApiError>} The query
 * result.
 */
export function useAuditEvent(auditEventId: string) {
  return useQuery<AuditEventDetail, ApiError>({
    queryKey: AUDIT_QUERY_KEYS.detail(auditEventId),
    queryFn: () => getAuditEvent(auditEventId),
    enabled: auditEventId !== '',
    staleTime: DETAIL_STALE_TIME_MS,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
